import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine } from '../contexts/BusinessLineContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  LogIn,
  FileText,
  Users,
  User,
  Target,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Activity,
  Shield,
  UserCheck,
  BookOpen,
  Mail,
  CreditCard,
  Loader2,
  CalendarClock,
  FileSpreadsheet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIF_TYPE_COLORS = {
  payment:                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  renewal:                'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  renovacion_proxima:     'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  lead_birthday:          'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
  client_birthday:        'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  lead_follow_up:         'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  followup_scheduled:     'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  overdue:                'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  oplus_aportacion:       'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  cedula_rc_vencimiento:  'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  cedula_vencimiento:     'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  rc_vencimiento:         'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200',
  daily_summary:          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  webhook_lead:           'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  referral_request:       'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  agent_birthday_self:    'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  agent_birthday_admin:   'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  policy_anniversary:     'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  meeting_reminder:       'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  _default:               'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const EVENT_CONFIG = {
  login:         { label: 'Inicio de sesión', icon: LogIn,      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  policy_create: { label: 'Póliza creada',    icon: FileText,   color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  policy_edit:   { label: 'Póliza editada',   icon: FileText,   color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  policy_import: { label: 'Importación Excel', icon: FileSpreadsheet, color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' },
  client_create: { label: 'Cliente creado',   icon: Users,      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
  client_edit:   { label: 'Cliente editado',  icon: Users,      color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200' },
  lead_create:   { label: 'Lead creado',      icon: Target,     color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  lead_edit:     { label: 'Lead editado',     icon: Target,     color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200' },
  rag_query:     { label: 'Consulta RAG',     icon: BookOpen,   color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' },
};

// ─── KPI Card ─────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon: Icon, color = 'primary' }) => {
  const colorClasses = {
    primary:   'bg-[#1a365d] text-white',
    secondary: 'bg-[#2d8659] text-white',
    accent:    'bg-[#6b7280] text-white',
    warning:   'bg-amber-500 text-white',
  };
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">{value ?? '—'}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

function fmtDate(iso) {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return new Date(iso + 'T12:00:00Z').toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      timeZone: 'America/Mexico_City',
    });
  }
  const hasOffset = iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso);
  const s = hasOffset ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Mexico_City',
  });
}

// ─── Fila de evento ───────────────────────────────────────────────────
function eventDetail(ev) {
  const meta = ev.metadata || {};
  if (ev.event_type === 'login') {
    return ev.ip_address || '—';
  }
  if (ev.event_type === 'policy_edit') {
    if (meta.policy_number) {
      return `Póliza ${meta.policy_number}${meta.client_name ? ` — ${meta.client_name}` : ''}`;
    }
    return '—';
  }
  if (ev.event_type === 'policy_import') {
    const parts = [];
    if (meta.imported != null) parts.push(`${meta.imported} importadas`);
    if (meta.updated != null) parts.push(`${meta.updated} actualizadas`);
    const summary = parts.length > 0 ? parts.join(', ') : '—';
    return meta.filename ? `${summary} (${meta.filename})` : summary;
  }
  return '—';
}

function EventRow({ ev }) {
  const conf = EVENT_CONFIG[ev.event_type] || { label: ev.event_type, icon: Activity, color: 'bg-gray-100 text-gray-700' };
  const Icon = conf.icon;
  return (
    <TableRow>
      <TableCell>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${conf.color}`}>
          <Icon className="h-3 w-3" />
          {conf.label}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <UserCheck className="h-3.5 w-3.5" />
          {ev.user_name || '—'}
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {eventDetail(ev)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
        {fmtDate(ev.created_at)}
      </TableCell>
    </TableRow>
  );
}

// ─── Fila de agente (expandible con historial de acciones) ───────────
const AGENT_COLS = [
  { key: 'logins',         label: 'Logins' },
  { key: 'polizas_create', label: 'Pólizas +' },
  { key: 'polizas_edit',   label: 'Pólizas ✎' },
  { key: 'polizas_import', label: 'Importaciones' },
  { key: 'clients_create', label: 'Clientes +' },
  { key: 'leads_create',   label: 'Leads +' },
  { key: 'rag_query',      label: 'Consultas RAG' },
];
// chevron col (1) + agente col (1) + AGENT_COLS (7) = 9
const AGENT_TABLE_COLS = 1 + 1 + AGENT_COLS.length;

const AGENT_ACTIVITY_PAGE_SIZE = 10;

function AgentRow({ agent, dateRange }) {
  const [expanded, setExpanded]           = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(false);
  const [activityPage, setActivityPage]   = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);

  const totalActivityPages = Math.max(1, Math.ceil(activityTotal / AGENT_ACTIVITY_PAGE_SIZE));

  const loadActivity = useCallback(async (page = 1) => {
    setActivityLoading(true);
    setActivityError(false);
    try {
      const res = await axios.get(`${API}/activity/user/${agent.user_id}`, {
        params: {
          page, page_size: AGENT_ACTIVITY_PAGE_SIZE,
          start: dateRange?.start, end: dateRange?.end,
        },
      });
      setActivityItems(res.data.items || []);
      setActivityTotal(res.data.total || 0);
      setActivityPage(page);
    } catch {
      setActivityError(true);
      setActivityItems([]);
    } finally {
      setActivityLoading(false);
    }
  }, [agent.user_id, dateRange?.start, dateRange?.end]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    // Reload always when expanding (not just on first open)
    if (next) loadActivity(1);
  };

  return (
    <>
      <TableRow>
        <TableCell className="w-8 px-2">
          <button
            onClick={toggleExpand}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Ver historial de acciones"
          >
            {expanded
              ? <ChevronUp className="h-4 w-4" />
              : <ChevronDown className="h-4 w-4" />}
          </button>
        </TableCell>
        <TableCell>
          <p className="text-sm font-medium">{agent.user_name}</p>
          {agent.ultima_actividad && (
            <p className="text-xs text-muted-foreground">Última: {fmtDate(agent.ultima_actividad)}</p>
          )}
        </TableCell>
        {AGENT_COLS.map(c => (
          <TableCell key={c.key} className="text-center text-sm">
            {agent[c.key] || 0}
          </TableCell>
        ))}
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={AGENT_TABLE_COLS} className="bg-muted/30 px-6 py-3">
            {activityLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1a365d]" />
              </div>
            ) : activityError ? (
              <div className="flex items-center justify-center gap-2 py-2">
                <p className="text-xs text-red-500">Error al cargar actividad</p>
                <button onClick={() => loadActivity(activityPage)}
                  className="text-xs text-blue-500 underline">Reintentar</button>
              </div>
            ) : activityItems.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                Sin actividad registrada en este periodo
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">
                  Historial de acciones — {agent.user_name} ({activityTotal} total)
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 font-medium text-muted-foreground w-[140px]">Evento</th>
                      <th className="text-left py-1 font-medium text-muted-foreground">Detalle</th>
                      <th className="text-left py-1 font-medium text-muted-foreground w-[160px]">Fecha y hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityItems.map(ev => {
                      const conf = EVENT_CONFIG[ev.event_type] || { label: ev.event_type, icon: Activity, color: 'bg-gray-100 text-gray-700' };
                      const Icon = conf.icon;
                      return (
                        <tr key={ev.id} className="border-b border-muted/50 last:border-0">
                          <td className="py-1">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${conf.color}`}>
                              <Icon className="h-3 w-3" />
                              {conf.label}
                            </span>
                          </td>
                          <td className="py-1 text-muted-foreground">{eventDetail(ev)}</td>
                          <td className="py-1 text-muted-foreground whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {totalActivityPages > 1 && (
                  <div className="flex items-center gap-3 pt-1">
                    <Button variant="outline" size="sm"
                      disabled={activityPage === 1}
                      onClick={() => loadActivity(activityPage - 1)}
                      className="h-6 text-xs px-2">
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {activityPage} / {totalActivityPages}
                    </span>
                    <Button variant="outline" size="sm"
                      disabled={activityPage === totalActivityPages}
                      onClick={() => loadActivity(activityPage + 1)}
                      className="h-6 text-xs px-2">
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Tabla de agentes (reutilizable en tab día y rango) ───────────────
function AgentTable({ agents, loading, emptyMsg, dateRange }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Agente</TableHead>
          <TableHead className="text-center w-[80px]">Logins</TableHead>
          <TableHead className="text-center w-[80px]">Pólizas +</TableHead>
          <TableHead className="text-center w-[80px]">Pólizas ✎</TableHead>
          <TableHead className="text-center w-[100px]">Importaciones</TableHead>
          <TableHead className="text-center w-[80px]">Clientes +</TableHead>
          <TableHead className="text-center w-[80px]">Leads +</TableHead>
          <TableHead className="text-center w-[100px]">Consultas RAG</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {agents?.length === 0 ? (
          <TableRow>
            <TableCell colSpan={AGENT_TABLE_COLS} className="text-center py-8 text-muted-foreground">
              {emptyMsg || 'Sin actividad en este periodo'}
            </TableCell>
          </TableRow>
        ) : (
          agents?.map(a => <AgentRow key={a.user_id} agent={a} dateRange={dateRange} />)
        )}
      </TableBody>
    </Table>
  );
}

// ─── Página principal ─────────────────────────────────────────────────
export function ActivityPage() {
  const { user, isAdmin, isMaster } = useAuth();
  const { selectedLine, changeBusinessLine } = useBusinessLine();
  const [searchParams] = useSearchParams();
  const navigate  = useNavigate();

  // Corrección 2: doble protección — redirigir si no es admin ni master
  useEffect(() => {
    if (user && !['admin', 'master'].includes(user.role)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Tab día
  const [dashDate, setDashDate]     = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
  const [dashData, setDashData]     = useState(null);
  const [dashLoading, setDashLoading] = useState(true);

  // Tab rango
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date(Date.now() - 6 * 86400000);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
  });
  const [rangeEnd, setRangeEnd]     = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
  const [rangeData, setRangeData]   = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Tab mi actividad
  const [myItems, setMyItems]       = useState([]);
  const [myPage, setMyPage]         = useState(1);
  const [myTotal, setMyTotal]       = useState(0);
  const [myLoading, setMyLoading]   = useState(false);

  // F3: selector de usuario para admin
  const [selectedUserId, setSelectedUserId] = useState(null); // null = yo mismo (admin)
  const [usersList, setUsersList]           = useState([]);

  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return t === 'notifications' ? 'notificaciones' : 'hoy';
  });

  // Tab notificaciones
  const [notifDate, setNotifDate] = useState(
    () => searchParams.get('date') || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' })
  );
  const [notifData, setNotifData] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifExpanded, setNotifExpanded] = useState(new Set());
  const [resending, setResending] = useState(false);
  const [resendingAgent, setResendingAgent] = useState(null);
  const [notifDateOpen, setNotifDateOpen] = useState(false);
  const [runningDailySummary, setRunningDailySummary] = useState(false);
  const [runningPayment, setRunningPayment] = useState(false);
  const [runningRenovaciones, setRunningRenovaciones] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [sendingCustom, setSendingCustom] = useState(false);

  // ── Carga de datos ──────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await axios.get(`${API}/activity/dashboard`, { params: { date: dashDate } });
      setDashData(res.data);
    } catch {
      toast.error('Error al cargar actividad');
    } finally {
      setDashLoading(false);
    }
  }, [dashDate]);

  const loadRange = useCallback(async () => {
    setRangeLoading(true);
    try {
      const res = await axios.get(`${API}/activity/dashboard/range`, {
        params: { start: rangeStart, end: rangeEnd },
      });
      setRangeData(res.data);
    } catch {
      toast.error('Error al cargar rango');
    } finally {
      setRangeLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  const loadMyActivity = useCallback(async (page = 1, userId = selectedUserId) => {
    setMyLoading(true);
    try {
      const url = userId
        ? `${API}/activity/user/${userId}`
        : `${API}/activity/me`;
      const res = await axios.get(url, { params: { page, page_size: 20 } });
      setMyItems(res.data.items || []);
      setMyTotal(res.data.total || 0);
      setMyPage(page);
    } catch {
      toast.error('Error al cargar actividad');
    } finally {
      setMyLoading(false);
    }
  }, [selectedUserId]);

  const loadNotifData = useCallback(async (dateStr = notifDate) => {
    setNotifLoading(true);
    try {
      const res = await axios.get(`${API}/activity/notifications`, { params: { date: dateStr } });
      setNotifData(res.data || []);
    } catch {
      toast.error('Error al cargar notificaciones');
    } finally {
      setNotifLoading(false);
    }
  }, [notifDate]);

  const handleResendAll = async () => {
    setResending(true);
    try {
      const res = await axios.post(`${API}/activity/notifications/resend`, null, { params: { date: notifDate } });
      toast.success(`Correos enviados: ${res.data.sent}${res.data.skipped ? ` (${res.data.skipped} sin email)` : ''}`);
    } catch {
      toast.error('Error al reenviar correos');
    } finally {
      setResending(false);
    }
  };

  const handleResendAgent = async (agentId, agentName) => {
    setResendingAgent(agentId);
    try {
      await axios.post(`${API}/activity/notifications/resend`, null, { params: { date: notifDate, user_id: agentId } });
      toast.success(`Correo enviado a ${agentName}`);
    } catch {
      toast.error('Error al reenviar correo');
    } finally {
      setResendingAgent(null);
    }
  };

  const handleRunPayment = async () => {
    setRunningPayment(true);
    try {
      await axios.post(`${API}/notification-jobs/run-payment-reminder`);
      toast.success('Pólizas pendientes procesadas. Revisa la campana de notificaciones.');
      setTimeout(() => loadNotifData(notifDate), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar payment reminder');
    } finally {
      setRunningPayment(false);
    }
  };

  const handleRunDailySummary = async () => {
    setRunningDailySummary(true);
    try {
      await axios.post(`${API}/notification-jobs/run-daily-summary`);
      toast.success('Resumen diario ejecutado. Revisa la campana de notificaciones.');
      loadNotifData(notifDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar resumen diario');
    } finally {
      setRunningDailySummary(false);
    }
  };

  const handleRunRenovaciones = async () => {
    setRunningRenovaciones(true);
    try {
      await axios.post(`${API}/notification-jobs/run-renovacion-proxima`);
      toast.success('Renovaciones próximas procesadas. Revisa la campana de notificaciones.');
      setTimeout(() => loadNotifData(notifDate), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar renovaciones próximas');
    } finally {
      setRunningRenovaciones(false);
    }
  };

  const openAdminModal = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      const users = res.data || [];
      setAdminUsers(users.filter(u => ['admin', 'master'].includes(u.role) && u.is_active));
      setSelectedAdmins([]);
      setShowAdminModal(true);
    } catch {
      toast.error('Error al cargar usuarios');
    }
  };

  const openAgentModal = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setAllUsers(
        (res.data || [])
          .filter(u => u.is_active)
          .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es'))
      );
      setSelectedAgent('');
      setShowAgentModal(true);
    } catch {
      toast.error('Error al cargar usuarios');
    }
  };

  const handleSendAllToAdmins = async () => {
    setSendingCustom(true);
    try {
      await axios.post(`${API}/notification-jobs/send-payment-custom`, {
        mode: 'all_to_admins',
        recipient_ids: selectedAdmins,
      });
      toast.success('Enviado correctamente');
      setShowAdminModal(false);
      setTimeout(() => loadNotifData(notifDate), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSendingCustom(false);
    }
  };

  const handleSendAgentSpecific = async () => {
    setSendingCustom(true);
    try {
      await axios.post(`${API}/notification-jobs/send-payment-custom`, {
        mode: 'agent_specific',
        agent_id: selectedAgent,
      });
      toast.success('Pólizas del agente enviadas');
      setShowAgentModal(false);
      setTimeout(() => loadNotifData(notifDate), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSendingCustom(false);
    }
  };

  // Carga la lista de agentes/asesores/aspirantes (solo para admin, solo una vez)
  const loadUsersList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/agentes-lista`);
      // Filtrar admins — el backend ahora devuelve campo "role"
      const filtered = (res.data || []).filter(u => ['agente', 'asesor', 'aspirante'].includes(u.role));
      setUsersList(filtered);
    } catch {
      // silencioso
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (activeTab === 'rango') loadRange(); }, [activeTab, loadRange]);
  useEffect(() => {
    if (activeTab === 'notificaciones') loadNotifData(notifDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, notifDate]);
  useEffect(() => {
    if (activeTab === 'yo') {
      loadMyActivity(1, selectedUserId);
      loadUsersList();  // always reload — lista fresca en cada apertura del tab
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'notifications') {
      setActiveTab('notificaciones');
      const d = searchParams.get('date');
      if (d) setNotifDate(d);
    }
  }, [searchParams]);

  const kpis = dashData?.kpis || {};
  const myTotalPages = Math.max(1, Math.ceil(myTotal / 20));

  const selectedUserName = selectedUserId
    ? (usersList.find(u => u.user_id === selectedUserId)?.display_name || 'Usuario')
    : (user?.full_name || 'Yo');

  return (
    <div className="space-y-6" data-testid="activity-page">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Actividad del equipo</h1>
          <p className="text-muted-foreground">
            Monitorea logins y acciones CRUD de los agentes
          </p>
        </div>
      </div>

      {/* ── Tabs primarias ───────────────────────────────────────────── */}
      <div className="flex gap-1 border-b">
        {[
          { id: 'hoy',            label: 'Por día' },
          { id: 'rango',          label: 'Por rango' },
          { id: 'yo',             label: activeTab === 'yo' && selectedUserId ? `Actividad de ${selectedUserName}` : 'Mi actividad' },
          ...(isAdmin ? [{ id: 'notificaciones', label: 'Notificaciones' }] : []),
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'text-[#1a365d] border-[#1a365d] bg-transparent dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ══ TAB: Por día ══════════════════════════════════════════════ */}
      {activeTab === 'hoy' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="date"
              value={dashDate}
              onChange={e => setDashDate(e.target.value)}
              className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9"
            />
            <Button variant="outline" size="sm" onClick={loadDashboard} disabled={dashLoading}>
              <RefreshCw className={`h-4 w-4 ${dashLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Select value={selectedLine} onValueChange={changeBusinessLine}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las aseguradoras</SelectItem>
                <SelectItem value="allianz">Allianz</SelectItem>
                <SelectItem value="insignia">Insignia Life</SelectItem>
                <SelectItem value="qualitas">Quálitas</SelectItem>
                <SelectItem value="flipping">Bienes Raíces</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {dashLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
            </div>
          ) : dashData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard title="Agentes activos"  value={kpis.agentes_activos}  icon={Users}     color="primary" />
                <KpiCard title="Total acciones"   value={kpis.total_acciones}   icon={Activity}  color="secondary" />
                <KpiCard title="Pólizas creadas"  value={kpis.polizas_creadas}  icon={FileText}  color="accent" />
                <KpiCard title="Clientes creados" value={kpis.clientes_creados} icon={Shield}    color="warning" />
                <KpiCard title="Leads creados"    value={kpis.leads_creados}    icon={Target}    color="primary" />
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Actividad por agente — {dashDate}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <AgentTable agents={dashData.by_agent} loading={false} dateRange={{ start: dashDate, end: dashDate }} />
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      )}

      {/* ══ TAB: Por rango ════════════════════════════════════════════ */}
      {activeTab === 'rango' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Desde</span>
              <input
                type="date"
                value={rangeStart}
                onChange={e => setRangeStart(e.target.value)}
                className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Hasta</span>
              <input
                type="date"
                value={rangeEnd}
                onChange={e => setRangeEnd(e.target.value)}
                className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9"
              />
            </div>
            <Button
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              size="sm"
              onClick={loadRange}
              disabled={rangeLoading}
            >
              {rangeLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : 'Consultar'}
            </Button>
            <Select value={selectedLine} onValueChange={changeBusinessLine}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las aseguradoras</SelectItem>
                <SelectItem value="allianz">Allianz</SelectItem>
                <SelectItem value="insignia">Insignia Life</SelectItem>
                <SelectItem value="qualitas">Quálitas</SelectItem>
                <SelectItem value="flipping">Bienes Raíces</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {rangeLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
            </div>
          ) : rangeData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard title="Agentes activos"  value={rangeData.kpis?.agentes_activos}  icon={Users}    color="primary" />
                <KpiCard title="Total acciones"   value={rangeData.kpis?.total_acciones}   icon={Activity} color="secondary" />
                <KpiCard title="Pólizas creadas"  value={rangeData.kpis?.polizas_creadas}  icon={FileText} color="accent" />
                <KpiCard title="Leads creados"    value={rangeData.kpis?.leads_creados}    icon={Target}   color="warning" />
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Actividad por agente — {rangeData.start} → {rangeData.end}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <AgentTable agents={rangeData.by_agent} loading={false} dateRange={{ start: rangeStart, end: rangeEnd }} />
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/30" />
              <div>
                <p className="font-medium text-muted-foreground">Selecciona un rango de fechas</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Elige fecha de inicio y fin, luego presiona Consultar
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: Mi actividad ═════════════════════════════════════════ */}
      {activeTab === 'yo' && (
        <div className="space-y-4">

          {/* F3: selector de usuario (solo admin) */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-muted-foreground whitespace-nowrap">Ver actividad de:</label>
            <select
              value={selectedUserId || ''}
              onChange={e => {
                const uid = e.target.value || null;
                setSelectedUserId(uid);
                loadMyActivity(1, uid);
              }}
              className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground h-9 min-w-[200px]"
            >
              <option value="">Mi actividad</option>
              {usersList.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.display_name}</option>
              ))}
            </select>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Historial de {selectedUserName} — {myTotal} evento{myTotal !== 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {myLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
                </div>
              ) : myItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                  <Activity className="h-12 w-12 text-muted-foreground/30" />
                  <div>
                    <p className="font-medium text-muted-foreground">Sin actividad registrada</p>
                    <p className="text-sm text-muted-foreground/70 mt-1">
                      La actividad se registra al crear o editar pólizas, clientes y leads
                    </p>
                  </div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Evento</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Detalle</TableHead>
                      <TableHead className="w-[180px]">Fecha y hora</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myItems.map(ev => <EventRow key={ev.id} ev={ev} />)}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Paginación */}
          {myTotalPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-2">
              <Button
                variant="outline"
                size="sm"
                disabled={myPage === 1}
                onClick={() => loadMyActivity(myPage - 1)}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Página {myPage} de {myTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={myPage === myTotalPages}
                onClick={() => loadMyActivity(myPage + 1)}
                className="gap-1"
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab Notificaciones ───────────────────────────────────────── */}
      {activeTab === 'notificaciones' && (
        <div className="space-y-4">
          {/* Selector de fecha + botón Reenviar todo */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-muted-foreground whitespace-nowrap">Fecha:</label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={() => {
                  const d = new Date(notifDate + 'T12:00:00');
                  d.setDate(d.getDate() - 1);
                  setNotifDate(d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Popover open={notifDateOpen} onOpenChange={setNotifDateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 px-3 text-sm font-normal min-w-[110px] justify-center">
                    {(() => {
                      const MESES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
                      const d = new Date(notifDate + 'T12:00:00');
                      return `${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
                    })()}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={new Date(notifDate + 'T12:00:00')}
                    onSelect={(date) => {
                      if (date) setNotifDate(date.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
                      setNotifDateOpen(false);
                    }}
                    disabled={(date) => date > new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                disabled={notifDate >= new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' })}
                onClick={() => {
                  const d = new Date(notifDate + 'T12:00:00');
                  d.setDate(d.getDate() + 1);
                  setNotifDate(d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {(isAdmin || isMaster) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendAll}
                disabled={resending || notifData.length === 0}
              >
                <Mail className="h-4 w-4 mr-2" />
                {resending ? 'Enviando...' : 'Reenviar todo'}
              </Button>
            )}
            {(isAdmin || isMaster) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRunDailySummary}
                disabled={runningDailySummary}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${runningDailySummary ? 'animate-spin' : ''}`} />
                {runningDailySummary ? 'Ejecutando...' : 'Ejecutar resumen'}
              </Button>
            )}
            {(isAdmin || isMaster) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRunPayment}
                disabled={runningPayment}
              >
                {runningPayment
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <CreditCard className="h-4 w-4 mr-2" />
                }
                {runningPayment ? 'Ejecutando...' : 'Ejecutar pendientes'}
              </Button>
            )}
            {(isAdmin || isMaster) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRunRenovaciones}
                disabled={runningRenovaciones}
              >
                {runningRenovaciones
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <CalendarClock className="h-4 w-4 mr-2" />
                }
                {runningRenovaciones ? 'Ejecutando...' : 'Ejecutar renovaciones próximas'}
              </Button>
            )}
            {(isAdmin || isMaster) && (
              <Button variant="outline" size="sm" onClick={openAdminModal}>
                <Users className="h-4 w-4 mr-2" />
                Todas las pólizas a admins
              </Button>
            )}
            {(isAdmin || isMaster) && (
              <Button variant="outline" size="sm" onClick={openAgentModal}>
                <User className="h-4 w-4 mr-2" />
                Pólizas por agente
              </Button>
            )}
          </div>

          {notifLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
            </div>
          ) : notifData.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <Shield className="h-12 w-12 text-muted-foreground/30" />
                <p className="font-medium text-muted-foreground">Sin notificaciones para esta fecha</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {notifData.map(ag => {
                const isExp = notifExpanded.has(ag.agent_id);
                return (
                  <Card key={ag.agent_id} className="overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                      onClick={() => {
                        setNotifExpanded(prev => {
                          const next = new Set(prev);
                          isExp ? next.delete(ag.agent_id) : next.add(ag.agent_id);
                          return next;
                        });
                      }}
                    >
                      <span className="font-medium text-sm">{ag.agent_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full bg-[#1a365d] text-white px-2.5 py-0.5 text-xs font-semibold">
                          {ag.count}
                        </span>
                        {(isAdmin || isMaster) && (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); handleResendAgent(ag.agent_id, ag.agent_name); }}
                            disabled={resendingAgent === ag.agent_id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border border-input bg-background hover:bg-muted transition-colors disabled:opacity-50"
                          >
                            <Mail className="h-3 w-3" />
                            {resendingAgent === ag.agent_id ? 'Enviando...' : 'Reenviar'}
                          </button>
                        )}
                        {isExp
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        }
                      </div>
                    </button>
                    {isExp && (
                      <CardContent className="p-0 border-t">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="px-4 py-2 text-left font-medium text-muted-foreground w-[130px]">Tipo</th>
                              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Título</th>
                              <th className="px-4 py-2 text-left font-medium text-muted-foreground w-[140px]">Hora</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ag.notifications.map(n => (
                              <tr key={n.id} className="border-b last:border-0 hover:bg-muted/20">
                                <td className="px-4 py-2">
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${NOTIF_TYPE_COLORS[n.type] || NOTIF_TYPE_COLORS._default}`}>
                                    {n.type || 'sistema'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-foreground">{n.title || '—'}</td>
                                <td className="px-4 py-2 text-muted-foreground">
                                  {n.created_at ? new Date(n.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal A — Todas las pólizas a admins */}
      <Dialog open={showAdminModal} onOpenChange={setShowAdminModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar todas las pólizas pendientes</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto py-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <Checkbox
                checked={adminUsers.length > 0 && selectedAdmins.length === adminUsers.length}
                onCheckedChange={(v) =>
                  setSelectedAdmins(v ? adminUsers.map(u => u.id) : [])
                }
              />
              Seleccionar todos
            </label>
            <hr />
            {adminUsers.map(u => (
              <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox
                  checked={selectedAdmins.includes(u.id)}
                  onCheckedChange={(v) =>
                    setSelectedAdmins(prev =>
                      v ? [...prev, u.id] : prev.filter(id => id !== u.id)
                    )
                  }
                />
                {u.full_name}
                <span className="text-xs text-muted-foreground">({u.role})</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendAllToAdmins}
              disabled={selectedAdmins.length === 0 || sendingCustom}
              className="bg-[#1a365d] text-white hover:bg-[#1a365d]/90"
            >
              {sendingCustom ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal B — Pólizas por agente */}
      <Dialog open={showAgentModal} onOpenChange={setShowAgentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar pólizas de un agente</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un agente..." />
              </SelectTrigger>
              <SelectContent>
                {allUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name}
                    <span className="text-xs text-muted-foreground ml-1">({u.role})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAgentModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendAgentSpecific}
              disabled={!selectedAgent || sendingCustom}
              className="bg-[#1a365d] text-white hover:bg-[#1a365d]/90"
            >
              {sendingCustom ? 'Enviando...' : 'Enviar pólizas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
